import { supabase } from "@/lib/supabase";
import type { Json } from "@/types/database";
import type {
  InquiryRow,
  InquiryTemplateRow,
  InquiryTemplateUpdate,
  InquiryUpdate,
} from "@/types/domain";
import type { FieldConfig, SlotConfig } from "./contract";

export class InquiriesError extends Error {
  public override readonly cause?: unknown;
  // Lets screens react to known failures without parsing message text.
  public readonly code?: "duplicate_slug" | "stale" | "has_requests";

  constructor(
    message: string,
    options?: { cause?: unknown; code?: "duplicate_slug" | "stale" | "has_requests" },
  ) {
    super(message);
    this.name = "InquiriesError";
    if (options?.code !== undefined) {
      this.code = options.code;
    }
    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

async function getAuthUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw new InquiriesError("Failed to load session", { cause: error });
  }
  const userId = data.session?.user.id;
  if (userId === undefined) {
    throw new InquiriesError("Not authenticated");
  }
  return userId;
}

// ============================================================================
// Templates
// ============================================================================

export interface TemplateUpsertInput {
  name: string;
  slug: string;
  type: InquiryTemplateRow["type"];
  is_active: boolean;
  field_config: FieldConfig;
  slot_config: SlotConfig;
}

export interface TemplateWithCount extends InquiryTemplateRow {
  inquiry_count: number;
}

async function listTemplates(): Promise<TemplateWithCount[]> {
  const { data, error } = await supabase
    .from("inquiry_templates")
    .select("*, inquiries(count)")
    .order("created_at", { ascending: false });
  if (error) {
    throw new InquiriesError("Failed to load templates", { cause: error });
  }
  return data.map((row) => {
    const { inquiries, ...template } = row;
    return { ...template, inquiry_count: inquiries[0]?.count ?? 0 };
  });
}

async function getTemplate(id: string): Promise<InquiryTemplateRow> {
  const { data, error } = await supabase
    .from("inquiry_templates")
    .select("*")
    .eq("id", id)
    .single();
  if (error) {
    throw new InquiriesError("Failed to load template", { cause: error });
  }
  return data;
}

async function createTemplate(input: TemplateUpsertInput): Promise<InquiryTemplateRow> {
  const userId = await getAuthUserId();
  const { data, error } = await supabase
    .from("inquiry_templates")
    .insert({
      user_id: userId,
      name: input.name.trim(),
      slug: input.slug,
      type: input.type,
      is_active: input.is_active,
      field_config: input.field_config as unknown as Json,
      slot_config: input.slot_config as unknown as Json,
    })
    .select("*")
    .single();
  if (error) {
    throw templateWriteError("Failed to create template", error);
  }
  return data;
}

// expectedUpdatedAt makes the save conditional: a form changed elsewhere since
// the editor loaded it (another tab, or paused from the list) is not overwritten.
async function updateTemplate(
  id: string,
  input: Partial<TemplateUpsertInput>,
  expectedUpdatedAt?: string,
): Promise<InquiryTemplateRow> {
  const patch: InquiryTemplateUpdate = {};
  if (input.name !== undefined) patch.name = input.name.trim();
  if (input.slug !== undefined) patch.slug = input.slug;
  if (input.type !== undefined) patch.type = input.type;
  if (input.is_active !== undefined) patch.is_active = input.is_active;
  if (input.field_config !== undefined) patch.field_config = input.field_config;
  if (input.slot_config !== undefined) {
    patch.slot_config = input.slot_config as unknown as Json;
  }

  let query = supabase.from("inquiry_templates").update(patch).eq("id", id);
  if (expectedUpdatedAt !== undefined) query = query.eq("updated_at", expectedUpdatedAt);
  const { data, error } = await query.select("*").maybeSingle();
  if (error) {
    throw templateWriteError("Failed to update template", error);
  }
  if (!data) {
    throw new InquiriesError(
      "This form changed elsewhere since you opened it. Your edits are still here; reload to see the latest before saving again.",
      { code: "stale" },
    );
  }
  return data;
}

// The public link is globally unique; surface a clash on the field, not as a
// generic failure.
function templateWriteError(message: string, error: { code?: string }): InquiriesError {
  return error.code === "23505"
    ? new InquiriesError("This link name is already in use. Pick another.", {
        cause: error,
        code: "duplicate_slug",
      })
    : new InquiriesError(message, { cause: error });
}

// Best-effort removal of inquiry photos from the private inquiry-photos bucket.
// Callers remove the files after deleting the owning row, and that delete must
// never be reported as failed because of a storage problem — so both a returned {error} (swallowed by not
// inspecting it) and a thrown network failure (swallowed by the try/catch) are
// tolerated. An orphaned file is acceptable; a half-failed delete is not.
async function removeInquiryPhotos(paths: string[]): Promise<void> {
  if (paths.length === 0) return;
  try {
    await supabase.storage.from("inquiry-photos").remove(paths);
  } catch {
    // Orphaned files are acceptable; do not block the row delete.
  }
}

// Hard-delete a template and (via ON DELETE CASCADE on inquiries.template_id)
// every inquiry ever submitted through it — submitted, converted and declined
// alike. Converted orders are untouched: orders never reference inquiries (the
// link lives on inquiries.linked_order_id), so they keep their snapshotted
// client data and merely lose the inquiry back-link. Child inquiry photos live
// in the private inquiry-photos bucket and are NOT removed by the DB cascade, so
// gather their paths, delete the row, then remove the files (best-effort, see
// removeInquiryPhotos). A failed row delete therefore keeps every photo.
//
// TODO: replace this hard-delete with an archive/soft-delete state (preserves
// inquiries + order links, reversible). Hard delete kept for now to clear test
// templates from the live DB.
//
// `onlyIfEmpty`: the form page offers Delete only for forms without requests;
// a request that arrived since the page loaded must not be cascaded away.
async function deleteTemplate(id: string, options: { onlyIfEmpty?: boolean } = {}): Promise<void> {
  const { data: rows, error: fetchError } = await supabase
    .from("inquiries")
    .select("photo_paths")
    .eq("template_id", id);
  if (fetchError) {
    throw new InquiriesError("Failed to load template inquiries", { cause: fetchError });
  }
  if (options.onlyIfEmpty === true && rows.length > 0) {
    throw new InquiriesError(
      "This form has received a request since you opened it, so it wasn't deleted.",
      { code: "has_requests" },
    );
  }
  const { error } = await supabase.from("inquiry_templates").delete().eq("id", id);
  if (error) {
    throw new InquiriesError("Failed to delete template", { cause: error });
  }
  await removeInquiryPhotos(rows.flatMap((row) => row.photo_paths ?? []));
}

// Slug is globally unique (the public route has no user discriminator), so the
// check is unscoped. The DB unique index is the authoritative last line.
async function isSlugTaken(slug: string, excludeId?: string): Promise<boolean> {
  let query = supabase.from("inquiry_templates").select("id").eq("slug", slug);
  if (excludeId !== undefined) {
    query = query.neq("id", excludeId);
  }
  const { data, error } = await query.limit(1);
  if (error) {
    throw new InquiriesError("Failed to check slug", { cause: error });
  }
  return data.length > 0;
}

// ============================================================================
// Inquiries
// ============================================================================

export interface InquiryWithTemplate extends InquiryRow {
  template: { name: string } | null;
}

async function listInquiries(opts: { includeDeclined: boolean }): Promise<InquiryWithTemplate[]> {
  let query = supabase
    .from("inquiries")
    .select("*, template:inquiry_templates(name)")
    .order("submitted_at", { ascending: false });
  if (!opts.includeDeclined) {
    query = query.neq("status", "declined");
  }
  const { data, error } = await query;
  if (error) {
    throw new InquiriesError("Failed to load inquiries", { cause: error });
  }
  return data;
}

async function getInquiry(id: string): Promise<InquiryWithTemplate> {
  const { data, error } = await supabase
    .from("inquiries")
    .select("*, template:inquiry_templates(name)")
    .eq("id", id)
    .single();
  if (error) {
    throw new InquiriesError("Failed to load inquiry", { cause: error });
  }
  return data;
}

async function updateInquiry(id: string, patch: InquiryUpdate): Promise<InquiryRow> {
  const { data, error } = await supabase
    .from("inquiries")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();
  if (error) {
    throw new InquiriesError("Failed to update inquiry", { cause: error });
  }
  return data;
}

async function countSubmitted(): Promise<number> {
  const { count, error } = await supabase
    .from("inquiries")
    .select("id", { count: "exact", head: true })
    .eq("status", "submitted");
  if (error) {
    throw new InquiriesError("Failed to count inquiries", { cause: error });
  }
  return count ?? 0;
}

// The Order Composer's reference panel: which inquiry (if any) produced this
// order. linked_order_id is the single source of truth for the link.
async function getInquiryByOrderId(orderId: string): Promise<InquiryWithTemplate | null> {
  const { data, error } = await supabase
    .from("inquiries")
    .select("*, template:inquiry_templates(name)")
    .eq("linked_order_id", orderId)
    .maybeSingle();
  if (error) {
    throw new InquiriesError("Failed to load inquiry for order", { cause: error });
  }
  return data;
}

// Hard-delete an inquiry in any state. The inquiry row is the ONLY thing that
// links to its template (submission count) and its converted order
// (linked_order_id), so deleting the row cleanly unlinks both — the draft order
// itself is intentionally kept (the baker may have started working it). Uploaded
// photos live in the private inquiry-photos bucket; remove them after the row so
// a failed delete keeps them (best-effort, see removeInquiryPhotos).
async function deleteInquiry(id: string): Promise<void> {
  const { data: row, error: fetchError } = await supabase
    .from("inquiries")
    .select("photo_paths")
    .eq("id", id)
    .single();
  if (fetchError) {
    throw new InquiriesError("Failed to load inquiry", { cause: fetchError });
  }
  const { error } = await supabase.from("inquiries").delete().eq("id", id);
  if (error) {
    throw new InquiriesError("Failed to delete inquiry", { cause: error });
  }
  await removeInquiryPhotos(row.photo_paths ?? []);
}

// inquiry-photos is a private bucket — photos are viewed via signed URLs.
async function getPhotoUrls(paths: string[]): Promise<string[]> {
  if (paths.length === 0) return [];
  const { data, error } = await supabase.storage
    .from("inquiry-photos")
    .createSignedUrls(paths, 3600);
  if (error) {
    throw new InquiriesError("Failed to load photos", { cause: error });
  }
  return data
    .map((item) => item.signedUrl)
    .filter((url): url is string => url !== null && url !== "");
}

export const inquiriesRepository = {
  listTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  isSlugTaken,
  listInquiries,
  getInquiry,
  updateInquiry,
  deleteInquiry,
  countSubmitted,
  getInquiryByOrderId,
  getPhotoUrls,
} as const;
